import { findLast, keyBy } from "lodash";
import * as React from "react";
import { useMemo, useRef } from "react";
import { ItemKey } from "~/sharedComponents/list";
import { ItemAdditionalOptions } from "~/sharedComponents/list/BaseList";
import { AdditionalRow, AdditionalRowOptions, List, ListProps } from "~/sharedComponents/list/List";
import { useSearchInputForList } from "~/sharedComponents/list/useSearchForList";
import { getListItemHeight } from "~/sharedComponents/listRows";
import { usePopupStateGetter } from "~/sharedComponents/popover/PopoverPopupStateContext";
import { MultiSelectInput, useFocusMultiselectInput } from "~/sharedComponents/select/multiSelect/MultiSelectInput";
import {
    SelectedItems,
    SelectedItemsProps,
    useSelectedItems,
} from "~/sharedComponents/select/multiSelect/SelectedItems";

export type ItemProps<TItem> = Pick<ListProps<TItem>, "getItemKey" | "getItemLabel">;
type InputProps<TItem> = ItemProps<TItem> & {
    value: ItemKey[];
    options: TItem[];
    canCreate?: boolean;
};

type MultiselectInputProps = React.ComponentProps<typeof MultiSelectInput>;
export type MultiSelectListProps<TItem> = Pick<
    ListProps<TItem>,
    | "isLoading"
    | "listRowComponent"
    | "getItemKey"
    | "getItemLabel"
    | "getItemHeight"
    | "additionalRows"
    | "filterItem"
    | "pageSize"
    | "isExactMatch"
    | "isItemDisabled"
> &
    Pick<ListProps<TItem>, "onDelete" | "onSelectItem"> &
    InputProps<TItem> & {
        loadOptions?: F<void>;
        onCreate?: (name: string, callBack?: (newItem: TItem) => void) => void;
        /** Custom renderer for selected items in the popup's input. */
        renderSelectedItems?: (props: SelectedItemsProps<TItem>) => React.ReactElement;
        multiSelectInputClassName?: string;
        renderWarning?: () => React.ReactElement;
    } & Pick<MultiselectInputProps, "inputMaxLength"> &
    React.PropsWithChildren<{}>;

export const MultiSelectList = <TItem,>(props: MultiSelectListProps<TItem>) => {
    const {
        value,
        options,
        loadOptions,
        onSelectItem,
        canCreate,
        getItemKey,
        getItemLabel,
        getItemHeight = getListItemHeight,
        additionalRows,
        renderSelectedItems,
        onCreate,
        inputMaxLength = 251,
        multiSelectInputClassName,
        renderWarning,
        ...listProps
    } = props;

    const { focusInput, inputRef } = useFocusMultiselectInput();

    React.useEffect(() => {
        loadOptions?.();
    }, []);

    const { defaultAddRow, isCreating } = useDefaultAddRow(props);

    const finalAdditionalRows: AdditionalRow<any>[] = React.useMemo(
        () => (additionalRows ? [...additionalRows, defaultAddRow] : [defaultAddRow]),
        [defaultAddRow, additionalRows]
    );

    const getPopupState = usePopupStateGetter();
    const onSelectListItem = (item: TItem & ItemAdditionalOptions, e?: React.KeyboardEvent) => {
        if (item.addRow) {
            item.addAction(item, getPopupState().close);
        } else {
            onSelectItem(item);
        }
    };

    const isItemSelected = useIsItemSelected({ value, getItemKey });

    const { searchText, setSearchText, onSearchInputKeyDown, listRef } = useSearchInputForList<TItem>();

    const selectedItems = useSelectedItems({ value, options, getItemKey });

    const unselectLastItem = (e: React.KeyboardEvent) => {
        const item = findLast(selectedItems, x => !props.isItemDisabled?.(x));
        if (item) {
            onSelectItem(item);
        }
    };

    const selectedItemsProps = { onChange: onSelectItem, selectedItems, getItemKey, getItemLabel };
    return (
        <>
            <MultiSelectInput
                ref={inputRef}
                canCreate={canCreate}
                searchText={searchText}
                setSearchText={setSearchText}
                inputMaxLength={inputMaxLength}
                onKeyDown={onSearchInputKeyDown}
                onUnselectLastItem={unselectLastItem}
                className={multiSelectInputClassName}
                selectedItems={selectedItems}
                style={{ margin: 2 }} // same as for chips, see Chips.less
            >
                {renderSelectedItems?.(selectedItemsProps) ?? <SelectedItems {...selectedItemsProps} />}
            </MultiSelectInput>
            {renderWarning?.()}
            <div onMouseUp={focusInput}>
                <List<TItem>
                    {...listProps}
                    items={options}
                    additionalRows={finalAdditionalRows}
                    onSelectItem={onSelectListItem}
                    isCreating={isCreating}
                    getItemHeight={getItemHeight}
                    ref={listRef}
                    getItemKey={getItemKey}
                    getItemLabel={getItemLabel}
                    searchText={searchText}
                    getItemSelected={isItemSelected}
                    ignoreHomeEndNavigation={true}
                />
            </div>
        </>
    );
};

const useDefaultAddRow = <TItem,>({ canCreate, onSelectItem, onCreate }: MultiSelectListProps<TItem>) => {
    const [isCreating, setIsCreating] = React.useState(false);
    const addAction = useRef<AdditionalRowOptions<ItemAdditionalOptions>["addAction"]>();
    addAction.current = item => {
        if (isCreating) {
            return;
        }

        setIsCreating(true);

        onCreate(item.newValue, (newItem: TItem) => {
            onSelectItem(newItem);
            setIsCreating(false);
        });
    };
    const defaultAddRow = useMemo<AdditionalRow<any>>(
        () => ({
            options: {
                isAvailable: canCreate,
                showOnSearch: true,
                hideOnExactMatch: true,
                addAction: item => addAction.current(item),
            },
        }),
        [canCreate]
    );

    return { defaultAddRow, isCreating };
};

const useIsItemSelected = <TItem,>({
    value,
    getItemKey,
}: Pick<MultiSelectListProps<TItem>, "value" | "getItemKey">) => {
    const selectedKeys = useMemo(() => keyBy(value, x => x), [value]);

    return (item: TItem) => !!selectedKeys[getItemKey(item)];
};
